import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Settings, Key, Lock, CheckCircle, Copy, AlertTriangle } from "lucide-react";
import { API_URL } from "../config/api";

export default function OneTimeSetupPage() {
  const [setupKey, setSetupKey] = useState("");
  const [resetEmail, setResetEmail] = useState("rok.kobal@europacific.com");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleSetup = async (e) => {
    e.preventDefault();
    
    if (setupKey !== "EuropacificSetup2026!") {
      toast.error("Invalid setup key");
      return;
    }

    if (!newPassword || newPassword.length < 4) {
      toast.error("Please enter a password (min 4 characters)");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/auth/one-time-setup-2026`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          setup_key: setupKey,
          reset_email: resetEmail,
          new_password: newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        setResults(data);
        toast.success("Setup complete! You can now log in.");
      } else {
        toast.error(data.detail || "Setup failed");
      }
    } catch (error) {
      toast.error("Connection error. Make sure you've deployed the latest code.");
    } finally {
      setLoading(false);
    }
  };

  const copyAllPuks = () => {
    if (!results?.puks_generated) return;
    const text = results.puks_generated
      .map(u => `${u.email}: ${u.puk}`)
      .join("\n");
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("All PUKs copied to clipboard!");
    setTimeout(() => setCopied(false), 3000);
  };

  if (results) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 p-4">
        <div className="max-w-2xl mx-auto">
          <Card>
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <CardTitle className="text-2xl text-green-700">Setup Complete!</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {results.password_reset && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-800 mb-2">Password Reset</h3>
                  <p className="text-blue-700">
                    <strong>{results.password_reset.email}</strong> - Password has been reset.
                  </p>
                  {results.password_reset.puk && (
                    <p className="text-blue-700 mt-1">
                      PUK: <code className="bg-white px-2 py-1 rounded font-mono">{results.password_reset.puk}</code>
                    </p>
                  )}
                </div>
              )}

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-amber-800 flex items-center gap-2">
                    <Key className="w-4 h-4" />
                    All User PUKs ({results.puks_generated?.length || 0} users)
                  </h3>
                  <Button size="sm" variant="outline" onClick={copyAllPuks}>
                    {copied ? <CheckCircle className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                    {copied ? "Copied!" : "Copy All"}
                  </Button>
                </div>
                <div className="bg-white rounded-lg p-3 max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-1">Email</th>
                        <th className="text-left py-1">PUK</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.puks_generated?.map((u, i) => (
                        <tr key={i} className="border-b border-gray-100">
                          <td className="py-1 text-gray-700">{u.email}</td>
                          <td className="py-1 font-mono font-bold">{u.puk}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-amber-700 mt-2">
                  ⚠️ Save this list! Users need their PUK to reset passwords when email is down.
                </p>
              </div>

              <Button 
                onClick={() => window.location.href = "/login"}
                className="w-full"
                size="lg"
              >
                Go to Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Settings className="w-8 h-8 text-amber-600" />
          </div>
          <CardTitle className="text-xl">One-Time System Setup</CardTitle>
          <p className="text-sm text-gray-500 mt-2">
            This will generate PUKs for all users and reset your password.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSetup} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="setupKey" className="flex items-center gap-2">
                <Key className="w-4 h-4" />
                Setup Key
              </Label>
              <Input
                id="setupKey"
                type="password"
                placeholder="Enter setup key"
                value={setupKey}
                onChange={(e) => setSetupKey(e.target.value)}
                required
              />
              <p className="text-xs text-gray-500">
                Key: EuropacificSetup2026!
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="resetEmail">Your Email (to reset password)</Label>
              <Input
                id="resetEmail"
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword" className="flex items-center gap-2">
                <Lock className="w-4 h-4" />
                New Password
              </Label>
              <Input
                id="newPassword"
                type="password"
                placeholder="Enter your new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={4}
              />
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                <div className="text-amber-800">
                  <p className="font-semibold">This will:</p>
                  <ul className="list-disc list-inside mt-1 text-amber-700">
                    <li>Generate PUKs for all users</li>
                    <li>Reset your password</li>
                    <li>Show you all PUKs (save them!)</li>
                  </ul>
                </div>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full"
              disabled={loading}
            >
              {loading ? "Setting up..." : "Run Setup"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
